import { useMatches } from '@tanstack/react-router';
import { SessionSwitcher } from '@/components/auth/SessionSwitcher';
import { Glocke } from '@/components/layout/Glocke';
import { ReserviertKnopf } from '@/components/reserviert/ReserviertBlatt';
import { AblegenKnopf } from '@/components/layout/Ablage';
import { KopfWerkzeuge } from '@/components/layout/KopfWerkzeuge';
import { useKopfKontext } from '@/lib/kopfKontext';
import { ArrowLeft, ArrowUp } from 'lucide-react';

// Titel-Mapping anhand der Route. Minimal — jede neue Route-Spec haengt
// sich hier selbst ein, oder wir wechseln spaeter auf Route-Metadaten.
const TITEL: Record<string, string> = {
  '/': 'Dashboard',
  '/kanban': 'Kanban',
  '/einstellungen': 'Einstellungen'
};

function titelFuer(pathname: string): string {
  if (TITEL[pathname]) return TITEL[pathname];
  if (pathname.startsWith('/bestellungen/')) return 'Bestellung';
  return '';
}

type Props = {
  /**
   * `weit` — Kopfzeile mit Glocke und Benutzermenü (Schreibtisch, Tablet).
   * `mobil` — nur der Titel; Glocke und Nutzerwechsel sitzen unten in der
   *   Bottom-Navigation, wo der Daumen sie erreicht.
   */
  variante?: 'weit' | 'mobil';
};

export function Topbar({ variante = 'weit' }: Props) {
  const matches = useMatches();
  const last = matches[matches.length - 1];
  const pathname = last?.pathname ?? '/';

  if (variante === 'mobil') {
    return (
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-line bg-cream px-4">
        <div className="flex min-w-0 items-center gap-1">
          <LeisteZurueck pathname={pathname} />
          <h1 className="truncate font-serif text-base text-ink">{titelFuer(pathname)}</h1>
        </div>
        <div className="flex items-center gap-1">
          <AblegenKnopf kompakt />
          <ReserviertKnopf kompakt />
        </div>
      </header>
    );
  }

  return (
    // Drei Zonen (03.11.2026, Skizze A–D): links „Wo bin ich?", in der
    // Mitte die Werkzeuge, rechts „Ich". Die Leiste steht ohnehin fest —
    // gerollt wird nur <main> darunter (PageLayout) —, also ist alles hier
    // von selbst „sticky".
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-cream px-4">
      <LeisteZurueck pathname={pathname} />
      <KopfOrt pathname={pathname} />
      <KopfWerkzeuge />
      {/* Die Glocke steht links vom Benutzermenü: erst was los ist, dann
          wer gerade arbeitet. Beide gehören zur aktiven Sitzung und
          wechseln gemeinsam mit ihr. Ablegen nur als Symbol (Skizze, 5). */}
      <div className="flex shrink-0 items-center gap-1">
        <AblegenKnopf kompakt />
        <Glocke />
        <SessionSwitcher kompakt />
      </div>
    </header>
  );
}

/**
 * „Zurück" in der Leiste (E63, Betreiber 03.11.2026: „Der Zurück-Knopf
 * soll auch sticky sein, oder?"). Erscheint, sobald der Kopf der
 * Bestellung weggescrollt ist — mit ihm verschwindet auch der Knopf auf
 * der Seite aus dem Bild. Solange der Kopf zu sehen ist, steht er dort
 * ohnehin, und zweimal „Zurück" übereinander wäre einmal zu viel.
 *
 * Ruft die Funktion, die die Seite veröffentlicht (lib/kopfKontext.ts):
 * dasselbe Ziel (E49), dieselbe Nachfrage beim Verlassen (E48). Am
 * Telefon steht er in der schmalen Kopfzeile — auch die rollt nicht mit.
 */
function LeisteZurueck({ pathname }: { pathname: string }) {
  const { bestellung, kopfSichtbar, zurueck } = useKopfKontext();
  if (!pathname.startsWith('/bestellungen/') || bestellung === null || kopfSichtbar || zurueck === null) {
    return null;
  }
  return (
    <button
      type="button"
      data-testid="leiste-zurueck"
      onClick={zurueck}
      className="-ml-2 inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-md px-2 text-sm text-ink-soft hover:bg-cream-dark hover:text-akzent"
    >
      <ArrowLeft aria-hidden className="h-4 w-4" />
      Zurück
    </button>
  );
}

/**
 * Links in der Leiste: der Seitenname — und in einer Bestellung, sobald
 * ihr Kopf weggescrollt ist, Nummer · Kunde · Zustand (Skizze B). Ein
 * Tipp darauf rollt zurück nach oben. Solange der Kopf zu sehen ist,
 * stünde es doppelt da; dann bleibt es bei „Bestellung" (Skizze C).
 */
function KopfOrt({ pathname }: { pathname: string }) {
  const { bestellung, kopfSichtbar } = useKopfKontext();
  const inBestellung = pathname.startsWith('/bestellungen/') && bestellung !== null;
  if (inBestellung && !kopfSichtbar) {
    return (
      <button
        type="button"
        data-testid="kopf-bestellung"
        onClick={() => document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' })}
        title="Nach oben zum Kopf der Bestellung"
        className="flex min-h-[44px] min-w-0 max-w-[26rem] shrink items-center gap-2 rounded-md px-1 text-left hover:bg-cream-dark"
      >
        <span className="shrink-0 font-serif text-base text-akzent">{bestellung.bestellnr}</span>
        <span className="min-w-0 truncate text-sm text-ink">{bestellung.kunde}</span>
        {bestellung.zustand && (
          <span className="hidden shrink-0 rounded-full border border-akzent px-2 py-0.5 text-xs text-akzent lg:inline">
            {bestellung.zustand}
          </span>
        )}
        <ArrowUp aria-hidden className="h-4 w-4 shrink-0 text-ink-faint" />
      </button>
    );
  }
  const titel = titelFuer(pathname);
  return (
    <h1 className={`shrink-0 truncate font-serif text-lg text-ink ${titel === '' ? 'hidden' : 'max-w-[12rem] xl:max-w-[16rem]'}`}>
      {titel}
    </h1>
  );
}
